import {
  Box,
  Paper,
  Typography,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ContentCopy as ContentCopyIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { toast } from 'sonner';

interface PromptVariable {
  name: string;
  type: string;
  description: string;
  example?: string;
  usage_contexts?: string[];
}

interface PromptVariablesPanelProps {
  promptVariables: PromptVariable[];
  maxHeight?: string;
  useSticky?: boolean;
}

export function PromptVariablesPanel({ 
  promptVariables, 
  maxHeight = '700px',
  useSticky = true 
}: PromptVariablesPanelProps) {
  if (promptVariables.length === 0) {
    return null;
  }

  return (
    <Paper 
      sx={{ 
        p: 2.5,
        height: '100%',
        maxHeight: maxHeight,
        overflowY: 'auto',
        ...(useSticky && {
          position: 'sticky',
          top: 20
        }),
        bgcolor: '#fafbfc',
        border: '2px solid #e2e8f0'
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <InfoIcon sx={{ color: '#3b82f6', fontSize: 20 }} />
        <Typography
          variant="subtitle2"
          sx={{
            fontWeight: 600,
            color: '#0f172a'
          }}
        >
          Prompt Variables
        </Typography>
        <Chip 
          label={promptVariables.length} 
          size="small" 
          sx={{ 
            bgcolor: '#dbeafe', 
            color: '#1e40af',
            fontWeight: 600,
            height: 20
          }} 
        />
      </Box>
      <Typography
        variant="caption"
        sx={{
          color: '#64748b',
          display: 'block',
          mb: 2,
          lineHeight: 1.5
        }}
      >
        Click any variable to copy it to your clipboard
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        {promptVariables.map((variable: PromptVariable, index: number) => (
          <Accordion 
            key={index}
            sx={{
              '&:before': { display: 'none' },
              boxShadow: 'none',
              bgcolor: 'transparent',
              '&.Mui-expanded': {
                margin: 0
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
              sx={{
                bgcolor: '#ffffff',
                border: '1px solid #e2e8f0',
                borderRadius: 1,
                mb: 1,
                minHeight: 'auto',
                '&:hover': {
                  bgcolor: '#f8fafc',
                  borderColor: '#cbd5e1'
                },
                '& .MuiAccordionSummary-content': {
                  margin: '8px 0',
                  flexDirection: 'column',
                  gap: 0.5
                },
                '&.Mui-expanded': {
                  minHeight: 'auto',
                  borderBottomLeftRadius: 0,
                  borderBottomRightRadius: 0
                }
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
                <Tooltip title="Click to copy" placement="top">
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      bgcolor: '#f0f9ff',
                      border: '1px solid #bae6fd',
                      px: 1,
                      py: 0.5,
                      borderRadius: 0.5,
                      cursor: 'pointer',
                      width: 'fit-content',
                      transition: 'all 0.2s',
                      '&:hover': {
                        bgcolor: '#e0f2fe',
                        borderColor: '#7dd3fc'
                      }
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      navigator.clipboard.writeText(`{${variable.name}}`);
                      toast.success('Variable copied!');
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        color: '#0c4a6e',
                        fontFamily: 'monospace',
                        fontSize: '12px'
                      }}
                    >
                      {`{${variable.name}}`}
                    </Typography>
                    <ContentCopyIcon sx={{ fontSize: 12, color: '#0369a1' }} />
                  </Box>
                </Tooltip>
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  <Chip
                    label={variable.type}
                    size="small"
                    sx={{
                      bgcolor: '#dbeafe',
                      color: '#1e40af',
                      fontWeight: 600,
                      fontSize: '10px',
                      height: 18
                    }}
                  />
                  {variable.usage_contexts && variable.usage_contexts.length > 0 && (
                    variable.usage_contexts.map((context: string, idx: number) => (
                      <Chip
                        key={idx}
                        label={context}
                        size="small"
                        sx={{
                          bgcolor: '#fef3c7',
                          color: '#92400e',
                          fontSize: '9px',
                          height: 18,
                          '& .MuiChip-label': {
                            px: 0.75
                          }
                        }}
                      />
                    ))
                  )}
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails 
              sx={{ 
                bgcolor: '#ffffff', 
                border: '1px solid #e2e8f0',
                borderTop: 'none',
                borderBottomLeftRadius: 1,
                borderBottomRightRadius: 1,
                mb: 1,
                pt: 1.5,
                pb: 1.5
              }}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#64748b',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      fontSize: '9px',
                      display: 'block',
                      mb: 0.5
                    }}
                  >
                    Description
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#334155',
                      fontSize: '12px',
                      lineHeight: 1.5
                    }}
                  >
                    {variable.description}
                  </Typography>
                </Box>
                {variable.example && (
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#64748b',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                        fontSize: '9px',
                        display: 'block',
                        mb: 0.5
                      }}
                    >
                      Example
                    </Typography>
                    <Paper
                      sx={{
                        bgcolor: '#f8fafc',
                        border: '1px solid #e2e8f0',
                        borderRadius: 0.5,
                        p: 1
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#475569',
                          fontFamily: 'monospace',
                          fontSize: '11px',
                          whiteSpace: 'pre-wrap',
                          lineHeight: 1.5
                        }}
                      >
                        {variable.example}
                      </Typography>
                    </Paper>
                  </Box>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Paper>
  );
}
